/**
 * Hook to fetch and use club settings
 * Uses react-query for caching and automatic refetching
 */

import { useOrganization } from '../contexts/organization-context';
import { useQuery } from '@tanstack/react-query';
import { fetchWithBypass } from '../utils/fetch';
import { logger } from '../utils/logger';

export interface ClubSettings {
  id: string;
  name: string;
  additional_seats: number;
  // Add other club settings fields as needed
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Hook to get club settings from database
 */
export function useClubSettings() {
  const { organization } = useOrganization();

  const {
    data: clubSettings,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clubSettings', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      const response = await fetchWithBypass(`/api/settings?organizationId=${organization.id}`);

      if (!response.ok) {
        const errorData = await response.json();

        // 401 Unauthorized means user is not logged in (will be redirected by LayoutWrapper)
        // Return null silently to avoid console errors during redirect
        if (response.status === 401) {
          logger.log(
            '[useClubSettings] User not authenticated, returning null (redirect in progress)'
          );
          return null;
        }

        // Log other errors normally
        logger.error('[useClubSettings] Error fetching club settings:', errorData);
        throw new Error(errorData.error || 'Failed to fetch club settings');
      }

      const { club } = await response.json();
      return club as ClubSettings;
    },
    enabled: !!organization?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401 errors (user not logged in)
      const errorMessage = (error as Error | undefined)?.message || '';
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('No valid session')) {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });

  return {
    clubSettings,
    isLoading,
    error,
    refetch,
    additionalSeats: clubSettings?.additional_seats || 0,
  };
}
